import React from 'react'
import { styled } from '@mui/material/styles'
import { required } from 'react-admin'
import { ClauseCommon, Rule, RuleTargetType } from 'services/api/entities/Rules'
import { serviceDiscriminatorChoices, TypedChoices } from 'services/cfg/form'
import {
  NAMES_DISCRIMINATORS_MAP,
  PACKAGE_NAME_DISCRIMINATORS_MAP,
} from 'services/utils/name-maps'
import {
  FormDataConsumerProps,
  ProductServiceDiscriminator,
} from 'types/common'
import CustomReferenceInput, {
  isProductVisibleInStore,
} from 'ui-shared/CustomInputs/CustomReferenceInput'
import LocalizedAutoCompleteArrayInput from 'ui-shared/LocalizedFields/LocalizedAutoCompleteArrayInput'
import KToolTip from 'ui-shared/KTooltip'
import { useFormContext } from 'react-hook-form'
import KSelectInput from 'ui-shared/WrappedInputs/KSelectInput'

const PREFIX = 'ProductsByServiceSelector'

const classes = {
  dFlexRow: `${PREFIX}-dFlexRow`,
  inputW250px: `${PREFIX}-inputW250px`,
  inputW800px: `${PREFIX}-inputW800px`,
  w100: `${PREFIX}-w100`,
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
  [`& .${classes.dFlexRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    width: '100%',
  },
  [`& .${classes.inputW250px}`]: {
    width: '250px',
  },
  [`& .${classes.inputW800px}`]: {
    width: '800px',
  },
  [`& .${classes.w100}`]: {
    width: '100%',
  },
})

type ScopedFormDataClause = ClauseCommon & { targetType?: RuleTargetType }

type Props = Omit<
  FormDataConsumerProps<Rule, ScopedFormDataClause>,
  'formData'
> & {
  serviceChoices?: TypedChoices<ProductServiceDiscriminator>
  serviceTooltipText: string
  productsTooltipText: string
}

/**
 * This components displays the service selector and the products selector of that service
 * @param serviceDiscriminatorEntityMap the map of entity and service discriminator to get through the
 * service the correct entity to pass to the custom reference input
 * @param serviceChoices the available service choices by default all
 */
const ProductsByServiceSelector: React.FC<Props> = ({
  serviceChoices = serviceDiscriminatorChoices,
  serviceTooltipText,
  productsTooltipText,
  scopedFormData,
  getSource,
}) => {
  // we use this form to clear the product choices whenever the service changes
  // because the ids selected in the form will be wrong to the new fetched products
  const { setValue } = useFormContext()

  // the product entity or package entity map
  const entityMap =
    scopedFormData?.targetType === 'package'
      ? PACKAGE_NAME_DISCRIMINATORS_MAP
      : NAMES_DISCRIMINATORS_MAP

  if (!scopedFormData || !getSource) return null

  // with the correct map we can access our entity to fetch the correct products
  const selectedEntity =
    entityMap[scopedFormData.service as keyof typeof entityMap]
  return (
    <Root>
      <KToolTip title={serviceTooltipText} placement="top">
        <KSelectInput
          label="form.fields.service"
          className={classes.inputW250px}
          source={getSource('service')}
          choices={serviceChoices}
          validate={required()}
          onChange={() => setValue(getSource('products'), [])}
        />
      </KToolTip>
      <KToolTip title={productsTooltipText} placement="top">
        <div className={classes.inputW800px}>
          <CustomReferenceInput
            entity={selectedEntity}
            filterFns={[isProductVisibleInStore]}
          >
            <LocalizedAutoCompleteArrayInput
              label="form.fields.products"
              source={getSource('products')}
              validate={required()}
              fullWidth
            />
          </CustomReferenceInput>
        </div>
      </KToolTip>
    </Root>
  )
}

export default ProductsByServiceSelector
